// File: frontend/src/pages/reports/ExamReport.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import { exportToPDF } from '../../utils/pdfExport';
import api from '../../utils/axios';

const ExamReportPage = () => {
  const { school } = useSchool();
  const [selectedExam, setSelectedExam] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get('/api/v1/reports/exams', {
          params: { exam_id: selectedExam }
        });
        if (res.data?.success) {
          setData(res.data.data);
          if (res.data.data?.exams?.length > 0 && !selectedExam) {
            setSelectedExam(res.data.data.exams[0].id);
          }
        } else {
          setError(res.data?.message || 'Failed to load examination report.');
        }
      } catch (err) {
        console.error('Error loading exam report:', err);
        setError(err.response?.data?.message || err.message || 'Error loading examination report.');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [selectedExam]);

  /**
   * Export Examination Report to PDF
   */
  const handleExportPDF = () => {
    if (!data || !data.class_performance) return;

    const currentExamObj = data.exams?.find((e) => String(e.id) === String(selectedExam));
    const title = `Examination Performance Report - ${currentExamObj?.name || 'Term Exam'}`;
    const headers = ['Class', 'Total Students', 'Pass Count', 'Fail Count', 'Pass %', 'Average Marks'];
    const rows = data.class_performance.map((c) => {
      const total = c.total_students || 1;
      const pass = c.pass_count || 0;
      const passPct = Math.round((pass / total) * 100);
      return [
        `Class ${c.class_id}`,
        c.total_students || 0,
        c.pass_count || 0,
        c.fail_count || 0,
        `${passPct}%`,
        parseFloat(c.average_marks || 0).toFixed(1)
      ];
    });

    exportToPDF(title, headers, rows, school?.name);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/reports" className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Examination Results Report</h1>
              <p className="text-xs text-slate-500">Class performance & top student leaderboards</p>
            </div>
          </div>

          <button
            onClick={handleExportPDF}
            disabled={loading || !data}
            className="bg-primary hover:bg-blue-600 text-white font-bold px-5 py-2.5 rounded-xl text-xs shadow-md transition disabled:opacity-50 flex items-center gap-2"
          >
            📄 Export PDF Report
          </button>
        </div>
      </header>

      {/* Main Body */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Exam Select Filter */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h2 className="font-bold text-slate-900 text-base">Select Examination Term</h2>
          <select
            value={selectedExam}
            onChange={(e) => setSelectedExam(e.target.value)}
            className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 w-full sm:w-64"
          >
            {data?.exams?.map((e) => (
              <option key={e.id} value={e.id}>{e.name} ({e.term} - {e.year})</option>
            ))}
          </select>
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-2xl text-sm font-semibold">
            {error}
          </div>
        )}

        {/* Top Performers Leaderboard Card */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 text-white shadow-md">
          <h2 className="font-extrabold text-base tracking-wide uppercase text-amber-400 mb-4 flex items-center gap-2">
            🏆 Top 5 Student Performers
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
            {loading ? (
              <div className="col-span-5 text-center text-slate-400 text-xs py-4">Loading top performers...</div>
            ) : !data?.top_performers || data.top_performers.length === 0 ? (
              <div className="col-span-5 text-center text-slate-400 text-xs py-4">No results recorded for this exam yet.</div>
            ) : (
              data.top_performers.map((st, idx) => (
                <div key={st.student_id} className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/10 space-y-1">
                  <span className="text-[10px] font-bold text-amber-300 uppercase tracking-widest block">Rank #{idx + 1}</span>
                  <p className="font-bold text-sm text-white truncate">{st.student_name}</p>
                  <p className="text-xs text-slate-300 font-mono">Class {st.class_id} • Roll {st.roll_no}</p>
                  <span className="inline-block mt-2 font-mono text-sm font-black text-emerald-400">{st.total_marks_obtained} Marks</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Class-wise Results Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-slate-900 text-base">Class Performance Summary</h2>
            <span className="text-xs text-slate-400 font-semibold">{data?.class_performance?.length || 0} Classes</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-sans">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase">
                  <th className="py-3.5 px-6">Class</th>
                  <th className="py-3.5 px-6 text-center">Total Students</th>
                  <th className="py-3.5 px-6 text-center">Passed</th>
                  <th className="py-3.5 px-6 text-center">Failed</th>
                  <th className="py-3.5 px-6 text-center">Pass %</th>
                  <th className="py-3.5 px-6 text-right">Avg Marks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-800 font-medium">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="py-12 text-center text-slate-400 font-medium">Calculating exam statistics...</td>
                  </tr>
                ) : !data?.class_performance || data.class_performance.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-12 text-center text-slate-400 font-medium">No results entered for selected exam.</td>
                  </tr>
                ) : (
                  data.class_performance.map((c) => {
                    const total = c.total_students || 1;
                    const pass = c.pass_count || 0;
                    const passPct = Math.round((pass / total) * 100);
                    return (
                      <tr key={c.class_id} className="hover:bg-slate-50/80 transition">
                        <td className="py-3.5 px-6 font-bold text-slate-900 text-sm">Class {c.class_id}</td>
                        <td className="py-3.5 px-6 text-center font-bold text-slate-900">{c.total_students}</td>
                        <td className="py-3.5 px-6 text-center font-bold text-emerald-600">{c.pass_count}</td>
                        <td className="py-3.5 px-6 text-center font-bold text-red-600">{c.fail_count}</td>
                        <td className="py-3.5 px-6 text-center font-bold text-blue-600">{passPct}%</td>
                        <td className="py-3.5 px-6 text-right font-mono font-bold text-slate-900">{parseFloat(c.average_marks || 0).toFixed(1)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>
  );
};

export default ExamReportPage;
